/**
 * UX Polish and Accessibility Enhancements
 * Comprehensive accessibility improvements, keyboard navigation, and responsive design
 */

import React, { useEffect, useRef } from 'react';

// Focus Management Hook
export const useFocusManagement = (isOpen, autoFocus = true) => {
  const previousFocusRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousFocusRef.current = document.activeElement;
      
      if (autoFocus && containerRef.current) {
        // Focus the first focusable element in the container
        const focusableElements = containerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    }

    return () => {
      if (!isOpen && previousFocusRef.current) {
        // Restore focus to the previously focused element
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, autoFocus]);

  return containerRef;
};

// Keyboard Navigation Hook
export const useKeyboardNavigation = (onEscape, onEnter, onTab) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'Escape':
          if (onEscape) {
            event.preventDefault();
            onEscape(event);
          }
          break;
        case 'Enter':
          if (onEnter && (event.target.tagName === 'BUTTON' || event.target.role === 'button')) {
            event.preventDefault();
            onEnter(event);
          }
          break;
        case 'Tab':
          if (onTab) {
            onTab(event);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onEscape, onEnter, onTab]);
};

// Trap Focus Hook for Modal/Dialog Components
export const useFocusTrap = (isActive = true) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    const trapFocus = (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    container.addEventListener('keydown', trapFocus);
    return () => container.removeEventListener('keydown', trapFocus);
  }, [isActive]);

  return containerRef;
};

// Responsive Breakpoint Hook
export const useResponsiveBreakpoint = () => {
  const [breakpoint, setBreakpoint] = React.useState('desktop');

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setBreakpoint('mobile');
      } else if (width < 768) {
        setBreakpoint('tablet-sm');
      } else if (width < 1024) {
        setBreakpoint('tablet');
      } else if (width < 1280) {
        setBreakpoint('desktop');
      } else {
        setBreakpoint('desktop-xl');
      }
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  return breakpoint;
};

// Enhanced Button Component with Full Accessibility
export const AccessibleButton = React.forwardRef(({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingText = 'Loading...',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  className = '',
  ...props
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500'
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <button
      ref={ref}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-busy={loading}
      {...props}
    >
      {loading ? loadingText : children}
    </button>
  );
});

AccessibleButton.displayName = 'AccessibleButton';

// Enhanced Table Component with Keyboard Navigation
export const AccessibleTable = ({
  data = [],
  columns = [],
  onRowSelect,
  selectedRowId,
  caption,
  className = '',
  'aria-label': ariaLabel
}) => {
  const tableRef = useRef(null);
  const [focusedRow, setFocusedRow] = React.useState(-1);

  // Keyboard navigation for table rows
  const handleKeyDown = (event) => {
    if (!onRowSelect) return;

    const rowCount = data.length;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedRow(prev => Math.min(prev + 1, rowCount - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedRow(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case ' ':
        if (focusedRow >= 0 && focusedRow < rowCount) {
          event.preventDefault();
          onRowSelect(data[focusedRow], focusedRow);
        }
        break;
      case 'Home':
        event.preventDefault();
        setFocusedRow(0);
        break;
      case 'End':
        event.preventDefault();
        setFocusedRow(rowCount - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="overflow-x-auto">
      <table
        ref={tableRef}
        className={`min-w-full divide-y divide-gray-200 ${className}`}
        onKeyDown={handleKeyDown}
        tabIndex={onRowSelect ? 0 : -1}
        role="table"
        aria-label={ariaLabel}
      >
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-gray-50">
          <tr role="row">
            {columns.map((column, index) => (
              <th
                key={column.key || index}
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                aria-sort={column.sortable ? 'none' : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => (
            <tr
              key={row.id || rowIndex}
              role="row"
              tabIndex={onRowSelect ? 0 : -1}
              className={`
                ${focusedRow === rowIndex ? 'bg-blue-50 ring-2 ring-blue-500' : 'hover:bg-gray-50'}
                ${selectedRowId === row.id ? 'bg-blue-100' : ''}
                ${onRowSelect ? 'cursor-pointer focus:outline-none' : ''}
                transition-colors duration-150
              `}
              onClick={() => onRowSelect && onRowSelect(row, rowIndex)}
              aria-selected={selectedRowId === row.id}
            >
              {columns.map((column, colIndex) => (
                <td
                  key={column.key || colIndex}
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                  role="cell"
                >
                  {column.render ? column.render(row[column.key], row, rowIndex) : row[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Screen Reader Announcements
export const useScreenReader = () => {
  const announceRef = useRef(null);

  const announce = (message, priority = 'polite') => {
    if (!announceRef.current) {
      // Create announcement element if it doesn't exist
      const element = document.createElement('div');
      element.setAttribute('aria-live', priority);
      element.setAttribute('aria-atomic', 'true');
      element.className = 'sr-only';
      document.body.appendChild(element);
      announceRef.current = element;
    }

    // Clear previous message and set new one
    announceRef.current.textContent = '';
    setTimeout(() => {
      announceRef.current.textContent = message;
    }, 100);
  };

  return { announce };
};

// Enhanced Skip Links Component
export const SkipLinks = () => {
  return (
    <nav className="sr-only focus:not-sr-only" aria-label="Skip navigation">
      <ul className="fixed top-0 left-0 z-50 bg-blue-600 text-white p-2 rounded-br-md">
        <li>
          <a
            href="#main-content"
            className="block p-2 text-white hover:bg-blue-700 focus:bg-blue-700 rounded"
          >
            Skip to main content
          </a>
        </li>
        <li>
          <a
            href="#main-navigation"
            className="block p-2 text-white hover:bg-blue-700 focus:bg-blue-700 rounded"
          >
            Skip to navigation
          </a>
        </li>
        <li>
          <a
            href="#claims-table"
            className="block p-2 text-white hover:bg-blue-700 focus:bg-blue-700 rounded"
          >
            Skip to claims table
          </a>
        </li>
      </ul>
    </nav>
  );
};

// Responsive Grid Component
export const ResponsiveGrid = ({ 
  children, 
  cols = { xs: 1, sm: 2, md: 3, lg: 4 }, 
  gap = 4, 
  className = '' 
}) => {
  const gapClass = `gap-${gap}`;
  const colClasses = Object.entries(cols)
    .map(([breakpoint, colCount]) => {
      if (breakpoint === 'xs') return `grid-cols-${colCount}`;
      return `${breakpoint}:grid-cols-${colCount}`;
    })
    .join(' ');

  return (
    <div className={`grid ${colClasses} ${gapClass} ${className}`}>
      {children}
    </div>
  );
};

// High Contrast Mode Detection
export const useHighContrastMode = () => {
  const [isHighContrast, setIsHighContrast] = React.useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    setIsHighContrast(mediaQuery.matches);

    const handleChange = (e) => setIsHighContrast(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isHighContrast;
};

// Reduce Motion Preference Detection
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

export default {
  useFocusManagement,
  useKeyboardNavigation,
  useFocusTrap,
  useResponsiveBreakpoint,
  AccessibleButton,
  AccessibleTable,
  useScreenReader,
  SkipLinks,
  ResponsiveGrid,
  useHighContrastMode,
  useReducedMotion
};